import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from "lucide-react";

type ToastTone = "ok" | "warn" | "error" | "info";

interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  duration?: number;
}

interface ToastContextValue {
  notify: (toast: Omit<Toast, "id">) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}

const ICONS: Record<ToastTone, typeof CheckCircle2> = {
  ok: CheckCircle2,
  warn: AlertTriangle,
  error: XCircle,
  info: Info
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, number>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const notify = useCallback(
    (toast: Omit<Toast, "id">): string => {
      const id = Math.random().toString(36).slice(2, 10);
      const next: Toast = { duration: 4500, ...toast, id };
      setToasts((prev) => [...prev, next]);
      if (next.duration && next.duration > 0) {
        const t = window.setTimeout(() => dismiss(id), next.duration);
        timers.current.set(id, t);
      }
      return id;
    },
    [dismiss]
  );

  useEffect(() => {
    return () => {
      timers.current.forEach((t) => window.clearTimeout(t));
      timers.current.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ notify, dismiss }), [notify, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="sgs-toast-region" role="region" aria-label="Notifications" aria-live="polite">
          {toasts.map((t) => {
            const Icon = ICONS[t.tone];
            return (
              <div key={t.id} className={`sgs-toast sgs-toast--${t.tone}`} role="status">
                <Icon size={16} strokeWidth={1.75} aria-hidden className="sgs-toast__icon" />
                <div className="sgs-toast__body">
                  <span className="sgs-toast__title">{t.title}</span>
                  {t.description && <span className="sgs-toast__desc">{t.description}</span>}
                </div>
                <button
                  type="button"
                  className="sgs-toast__close"
                  aria-label="Dismiss notification"
                  onClick={() => dismiss(t.id)}
                >
                  <X size={14} strokeWidth={2} aria-hidden />
                </button>
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}
